import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ConversationHistoryEntry } from '@digital-twins/shared-types'
import { formatDistanceToNow } from 'date-fns'
import { MoreVertical, Pencil, Trash2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { DeleteStudyDialog } from '@/features/studies/list/page/components/DeleteStudyDialog'
import { cn } from '@/lib/utils'

interface ConversationCardProps {
  entry: ConversationHistoryEntry;
  onClick: () => void;
  onDelete?: (id: string) => void;
  onRename?: (id: string, title: string) => void;
  /** Controls delete confirmation copy — quick-study lobby vs conversations lobby. */
  deleteMode?: 'conversation' | 'quick-study';
}

function getInitials(title: string): string {
  return title
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
}

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return ''
  try {
    return formatDistanceToNow(new Date(dateStr), { addSuffix: true })
  } catch {
    return ''
  }
}

const COMPACT_TIME_UNITS: Array<{ unit: Intl.RelativeTimeFormatUnit; seconds: number }> = [
  { unit: 'year', seconds: 31536000 },
  { unit: 'month', seconds: 2592000 },
  { unit: 'week', seconds: 604800 },
  { unit: 'day', seconds: 86400 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
]

/** Short relative time ("10h ago") for the phone title line, where every character counts. */
function formatCompactRelativeTime(dateStr: string | null, locale: string): string {
  if (!dateStr) return ''
  const elapsedSeconds = (Date.now() - new Date(dateStr).getTime()) / 1000
  if (!Number.isFinite(elapsedSeconds)) return ''
  try {
    const format = new Intl.RelativeTimeFormat(locale, { style: 'narrow', numeric: 'always' })
    const match = COMPACT_TIME_UNITS.find((entry) => elapsedSeconds >= entry.seconds)
    if (!match) {
      return format.format(0, 'minute')
    }
    return format.format(-Math.floor(elapsedSeconds / match.seconds), match.unit)
  } catch {
    return ''
  }
}

const AVATAR_CONTAINER = 'h-12 w-12 shrink-0'

function AvatarStack(props: { images: Array<string>; title: string; count: number }) {
  const { images, title, count } = props
  const displayed = images.slice(0, 4)

  if (displayed.length === 0) {
    return (
      <div className={cn(AVATAR_CONTAINER, 'flex items-center justify-center rounded-full bg-muted text-sm font-medium text-muted-foreground')}>
        {getInitials(title)}
      </div>
    )
  }

  if (displayed.length === 1) {
    return (
      <img
        src={displayed[0]}
        alt=""
        className={cn(AVATAR_CONTAINER, 'rounded-full object-cover')}
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none'
        }}
      />
    )
  }

  const slots = displayed.slice(0, 4)
  const overflow = count > 4 ? count - 3 : 0
  const visibleSlots = overflow > 0 ? slots.slice(0, 3) : slots

  return (
    <div className={cn(AVATAR_CONTAINER, 'grid grid-cols-2 grid-rows-2 gap-0.5')}>
      {visibleSlots.map((src, i) => (
        <img
          key={i}
          src={src}
          alt=""
          className="h-full w-full rounded-full object-cover"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      ))}
      {overflow > 0 && (
        <span className="flex h-full w-full items-center justify-center rounded-full text-[9px] font-medium text-muted-foreground">
          +{overflow}
        </span>
      )}
    </div>
  )
}

export function ConversationCard(props: ConversationCardProps) {
  const { entry, onClick, onDelete, onRename, deleteMode = 'conversation' } = props

  const { t, i18n } = useTranslation('app')
  const relativeTime = formatRelativeTime(entry.lastMessageAt)
  const compactRelativeTime = formatCompactRelativeTime(entry.lastMessageAt, i18n.language)

  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(entry.title)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  // Guards against a second commit when Enter commits and the subsequent
  // unmount fires a trailing blur (both would otherwise call onRename).
  const settledRef = useRef(false)
  // The phone menu runs its action once it has closed, so its focus restore
  // cannot blur the rename input or race the delete dialog.
  const pendingMenuActionRef = useRef<'rename' | 'delete' | null>(null)

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus()
      inputRef.current?.select()
    }
  }, [isEditing])

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    setDeleteDialogOpen(true)
  }

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false)
  }

  const handleConfirmDelete = async () => {
    if (!onDelete) {
      return
    }
    setDeleting(true)
    try {
      await Promise.resolve(onDelete(entry.id))
      setDeleteDialogOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  const startRename = (e: React.MouseEvent) => {
    e.stopPropagation()
    settledRef.current = false
    setDraft(entry.title)
    setIsEditing(true)
  }

  const cancelRename = () => {
    settledRef.current = true
    setIsEditing(false)
    setDraft(entry.title)
  }

  const commitRename = () => {
    if (!isEditing || settledRef.current) return
    settledRef.current = true
    const next = draft.trim()
    setIsEditing(false)
    if (next && next !== entry.title) {
      onRename?.(entry.id, next)
    } else {
      setDraft(entry.title)
    }
  }

  const onMenuCloseAutoFocus = (event: Event) => {
    const action = pendingMenuActionRef.current
    pendingMenuActionRef.current = null
    if (!action) {
      return
    }
    event.preventDefault()
    if (action === 'rename') {
      settledRef.current = false
      setDraft(entry.title)
      setIsEditing(true)
    } else {
      setDeleteDialogOpen(true)
    }
  }

  // Below sm the hover-only actions cannot be reached by touch: one always
  // visible menu on the title line holds them instead.
  const mobileMenu = (onRename || onDelete) && !isEditing && (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <span
          role="button"
          tabIndex={0}
          aria-label={t('conversations.cardActions')}
          onClick={(e) => e.stopPropagation()}
          data-testid={`conversation-card-menu-${entry.id}`}
          className="-mr-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground sm:hidden"
        >
          <MoreVertical className="h-4 w-4" strokeWidth={1.5} />
        </span>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        onCloseAutoFocus={onMenuCloseAutoFocus}
      >
        {onRename && (
          <DropdownMenuItem
            onSelect={() => {
              pendingMenuActionRef.current = 'rename'
            }}
            data-testid={`conversation-card-menu-rename-${entry.id}`}
          >
            <Pencil className="mr-2 h-4 w-4" strokeWidth={1.5} />
            {t('conversations.rename')}
          </DropdownMenuItem>
        )}
        {onDelete && (
          <DropdownMenuItem
            onSelect={() => {
              pendingMenuActionRef.current = 'delete'
            }}
            data-testid={`conversation-card-menu-delete-${entry.id}`}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" strokeWidth={1.5} />
            {t('delete', { ns: 'common' })}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const renameButton = onRename && !isEditing && (
    <span
      role="button"
      tabIndex={-1}
      onClick={startRename}
      data-testid={`conversation-card-rename-${entry.id}`}
      className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
    >
      <Pencil className="h-3.5 w-3.5" strokeWidth={1.5} />
    </span>
  )

  return (
    <>
    <button
      type="button"
      onClick={onClick}
      data-testid={`conversation-card-${entry.id}`}
      className="group relative flex w-full items-center gap-3.5 border-b border-border px-4 py-3.5 text-left transition-colors hover:bg-muted/40"
    >
      <AvatarStack
        images={entry.participantImages}
        title={entry.title}
        count={entry.participantCount}
      />

      <div className="flex min-w-0 flex-1 gap-2">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            {isEditing ? (
              <input
                ref={inputRef}
                value={draft}
                maxLength={200}
                onChange={(e) => setDraft(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onPointerDown={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  e.stopPropagation()
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commitRename()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    cancelRename()
                  }
                }}
                onBlur={commitRename}
                data-testid={`conversation-card-rename-input-${entry.id}`}
                className="min-w-0 flex-1 rounded border border-border bg-background px-1.5 py-0.5 text-[15px] font-semibold leading-snug text-foreground outline-none focus:ring-1 focus:ring-ring"
              />
            ) : (
              <span className="truncate text-[15px] font-semibold leading-snug text-foreground">
                {entry.title}
              </span>
            )}
            {!isEditing && entry.subtitle && (
              <span className="hidden min-w-[3rem] max-w-[45%] truncate rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground sm:inline-block">
                {entry.subtitle}
              </span>
            )}
            {compactRelativeTime && (
              <span
                title={relativeTime}
                className="ml-auto shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:hidden"
                data-testid={`conversation-card-time-${entry.id}`}
              >
                {compactRelativeTime}
              </span>
            )}
            {mobileMenu}
          </div>

          {entry.lastMessagePreview && (
            <div className="flex min-w-0 items-baseline gap-2">
              <p className="line-clamp-1 min-w-0 flex-1 text-sm leading-snug text-muted-foreground sm:line-clamp-2">
                {entry.lastMessagePreview}
              </p>
            </div>
          )}

          {!entry.lastMessagePreview && relativeTime && (
            <div className="hidden items-center justify-end sm:flex">
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {relativeTime}
              </span>
            </div>
          )}
        </div>

        {entry.lastMessagePreview && (renameButton || onDelete || relativeTime) && (
          <div className="hidden shrink-0 flex-col items-end self-stretch sm:flex">
            {(renameButton || onDelete) && (
              <div className="flex items-center gap-1">
                {renameButton}
                {onDelete && (
                  <span
                    role="button"
                    tabIndex={-1}
                    onClick={handleDelete}
                    data-testid={`conversation-card-delete-${entry.id}`}
                    className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                  </span>
                )}
              </div>
            )}
            {relativeTime && (
              <span
                className={cn(
                  'hidden whitespace-nowrap text-xs text-muted-foreground sm:inline',
                  (renameButton || onDelete) && 'mt-auto',
                )}
              >
                {relativeTime}
              </span>
            )}
          </div>
        )}
      </div>

      {!entry.lastMessagePreview && (onRename || onDelete) && !isEditing && (
        <div className="absolute right-3 top-1/2 hidden -translate-y-1/2 items-center gap-1 sm:flex">
          {onRename && (
            <span
              role="button"
              tabIndex={-1}
              onClick={startRename}
              data-testid={`conversation-card-rename-${entry.id}`}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover:opacity-100"
            >
              <Pencil className="h-4 w-4" strokeWidth={1.5} />
            </span>
          )}
          {onDelete && (
            <span
              role="button"
              tabIndex={-1}
              onClick={handleDelete}
              data-testid={`conversation-card-delete-${entry.id}`}
              className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
            >
              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
            </span>
          )}
        </div>
      )}
    </button>

    {onDelete && (
      <DeleteStudyDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        studyName={entry.title}
        mode={deleteMode}
        deleting={deleting}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
    )}
    </>
  )
}
